<script setup lang="ts">
import { XIcon } from '@clicksign/excalibur';
import { useRouteQuery } from '@vueuse/router';
import { z } from 'zod';

import { useCurrentSignatureRequestKey, useEnvironmentInfo, useFeature, useFlux, AppFeature, Decisions, APP_ROUTES } from '~~core';
import { BrandButton, SecureEnvironment } from '~~base-ui';

import { useFetchFeedbackInfo } from '../../../repositories/feedback';
import FeedbackSuccessMessage from '../../../components/FeedbackSuccessMessage/FeedbackSuccessMessage.vue';
import CaptureFeedbackMessage from '../../../components/CaptureFeedbackMessage/CaptureFeedbackMessage.vue';
import FeedbackRefusalMessage from '../../../components/FeedbackRefusalMessage/FeedbackRefusalMessage.vue';
import FeedbackSuccessSignerAreaCTA from '../../../components/FeedbackSuccessSignerAreaCTA/FeedbackSuccessSignerAreaCTA.vue';
import FeedbackSuccessAccessDocumentsCTA from '../../../components/FeedbackSuccessAccessDocumentsCTA/FeedbackSuccessAccessDocumentsCTA.vue';

const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { feedback, isFetched: isFetchedFeedbackInfo } = useFetchFeedbackInfo(signatureRequestKey.value);
const { isEmbedded, isFromSignerArea } = useEnvironmentInfo();
const { isEnabled: isSignerAreaCTAEnabled } = useFeature(Decisions.SIGNER_AREA_CTA);
const isRefused = useRouteQuery('refused', 'false', { transform: (value: string) => value === 'true' });
const { isFlux } = useFlux();

const status = useRouteQuery('status', undefined, { transform: z.string().optional().parse });
const isDigitalCertifiedError = computed(() => status.value === 'digital_certificate_pdfa_error');
const isBiometricError = computed(() => status.value === 'biometric_error');
const isFacialBiometricsLimitExceeded = computed(() => status.value === 'facial_biometrics_limit_exceeded');

const { locale } = useI18n();

const isEsMx = computed(() => locale.value.toLocaleLowerCase() === 'es-mx');
const isFullySigned = computed(() => feedback.value?.status === 'closed');
const hasAccount = computed(() => !!feedback.value?.signer.isUser);

const showSignerAreaCTA = computed(() => !isFlux
  && !isFacialBiometricsLimitExceeded.value
  && !isBiometricError.value
  && !isDigitalCertifiedError.value
  && !isEmbedded.value
  && !isFromSignerArea.value
  && !isEsMx.value
  && isSignerAreaCTAEnabled.value);

const { t } = useI18n();
</script>

<template>
  <div
    v-if="isFetchedFeedbackInfo"
    :class="$style.root"
  >
    <div :class="$style.message">
      <CaptureFeedbackMessage
        v-if="!!status && status !== 'facial_biometrics_approved'"
        :status="status"
      />
      <FeedbackRefusalMessage v-else-if="!isFlux && isRefused" />
      <FeedbackSuccessMessage
        v-else
        :is-fully-signed="isFullySigned"
        :documents-count="2"
      />

      <!-- SIGNER_AREA_SHORTCUT -->
      <!-- TODO: Remove AppFeature, but keep FeedbackSuccessAccessDocumentsCTA component-->
      <AppFeature :name="Decisions.SIGNER_AREA_SHORTCUT">
        <FeedbackSuccessAccessDocumentsCTA
          v-if="!isFlux && !isEmbedded"
          :class="$style.accessDocumentsCTA"
          :has-account="hasAccount"
          :is-from-signer-area="isFromSignerArea"
        />

        <!-- SIGNER_AREA_SHORTCUT -->
        <!-- TODO: Remove template #off block -->
        <template #off>
          <div
            v-if="!isFlux && !isEmbedded && isFromSignerArea"
            :class="$style.goBackToSignerDashboardBtn"
          >
            <BrandButton
              full-width
              :to="APP_ROUTES.signerArea"
              external
              data-testid="signerDashboardButton"
            >
              <XIcon
                icon="arrow-left"
                left
              />

              {{ t('requirement.feedback.signerAreaCTA.goBackToSignerDashboard') }}
            </BrandButton>
          </div>
        </template>
      </AppFeature>

      <SecureEnvironment :class="$style.security" />
    </div>

    <div
      v-if="showSignerAreaCTA"
      :class="$style.CTAWrapper"
    >
      <FeedbackSuccessSignerAreaCTA
        :class="$style.CTA"
        :has-account="hasAccount"
        :has-pending-documents="feedback?.signer?.hasPendencies"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;

  // justify-content: center;
  flex: 1;
  flex-direction: column;
  grid-template-rows: auto 1fr;

  // overflow-y: hidden;
  gap: var(--space-large);
}

.accessDocumentsCTA {
  margin: 0;
}

// SIGNER_AREA_SHORTCUT

// TODO: Remove non-used class

.goBackToSignerDashboardBtn {
  width: 50%;
  max-width: 380px;
  margin: 0 auto var(--space-regular);
}

.message,
.CTA {
  animation: fade-in-up backwards ease-out 450ms;
}

.CTAWrapper {
  display: grid;
  place-items: flex-start;
  overflow: hidden;
  background-color: var(--color-neutral-50);
  animation: CTALoadInUp 600ms both ease-out;
  animation-delay: 1.95s;

  @include breakpoint('small') {
    place-items: center;
  }
}

.CTA {
  animation-delay: 2.5s;
}

.security {
  margin-top: var(--space-regular);
}

@keyframes CTALoadInUp {
  from {
    flex-basis: 0;
    opacity: 0;
    transform: translateY(100vh);
  }

  to {
    flex-basis: 100%;
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(50px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
